import { useEffect, useState } from 'react';
import { useModalStore } from '../../store/modalStore';
import { useToastStore } from '../../store/toastStore';
import {
  useVideoGroups,
  useCreateVideoGroup,
  useAddVideoToGroup,
} from '../../hooks/useVideos';
import type { Video } from '../../types/models';

export function AddToVideoGroupModal() {
  const { isOpen, modalType, modalData, closeModal, onSuccess } = useModalStore();
  const addToast = useToastStore((state) => state.addToast);

  const { data: groups = [] } = useVideoGroups();
  const createGroupMutation = useCreateVideoGroup();
  const addToGroupMutation = useAddVideoToGroup();

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const video = modalData as Video;
  const open = isOpen && modalType === 'addToVideoGroup' && !!video;

  useEffect(() => {
    if (!open) {
      setSelectedGroupId(null);
      setCreatingNew(false);
      setNewGroupTitle('');
      setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const canSave = creatingNew ? newGroupTitle.trim().length > 0 : selectedGroupId !== null;

  const handleSave = async () => {
    setSaving(true);
    try {
      let groupId = selectedGroupId;
      if (creatingNew) {
        const created = await createGroupMutation.mutateAsync({
          title: newGroupTitle.trim(),
        });
        groupId = created.id;
      }
      if (groupId === null) return;

      await addToGroupMutation.mutateAsync({ groupId, videoId: video.id });
      addToast('Added to group', 'success');
      onSuccess?.();
      closeModal();
    } catch (err) {
      addToast((err as Error).message || 'Failed to add video to group', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add to Group</h2>
          <button onClick={closeModal} className="btn-close" type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="collection-list">
            {groups.length === 0 && !creatingNew && (
              <div className="empty-state">No groups yet — create one below</div>
            )}
            {groups.map((group) => (
              <label
                key={group.id}
                className={`collection-item collection-item-selectable ${
                  !creatingNew && selectedGroupId === group.id ? 'is-selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="video-group"
                  checked={!creatingNew && selectedGroupId === group.id}
                  disabled={saving}
                  onChange={() => {
                    setCreatingNew(false);
                    setSelectedGroupId(group.id);
                  }}
                  className="collection-item-checkbox"
                />
                <div className="collection-item-content">
                  <div className="collection-item-name">{group.title}</div>
                  <div className="collection-item-meta">
                    {group.videoCount} {group.videoCount === 1 ? 'video' : 'videos'}
                  </div>
                </div>
              </label>
            ))}

            <label className={`collection-item collection-item-selectable ${creatingNew ? 'is-selected' : ''}`}>
              <input
                type="radio"
                name="video-group"
                checked={creatingNew}
                disabled={saving}
                onChange={() => {
                  setCreatingNew(true);
                  setSelectedGroupId(null);
                }}
                className="collection-item-checkbox"
              />
              <div className="collection-item-content">
                <div className="collection-item-name">+ New group</div>
              </div>
            </label>

            {creatingNew && (
              <div className="form-group">
                <label htmlFor="new-group-title">Group name</label>
                <input
                  id="new-group-title"
                  type="text"
                  value={newGroupTitle}
                  onChange={(e) => setNewGroupTitle(e.target.value)}
                  disabled={saving}
                  placeholder="Enter group name"
                  autoFocus
                />
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={closeModal} className="btn btn-secondary" type="button" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            className="btn btn-primary"
            type="button"
            disabled={saving || !canSave}
          >
            {saving ? 'Saving...' : 'Add to Group'}
          </button>
        </div>
      </div>
    </div>
  );
}
